import {
  BlogList, Col, Container, Row, Section, ShareButtons, Tags,
} from 'atoms'
// import StickyNode from 'react-stickynode'
import { Sticky, StickyContainer } from 'atoms/Sticky'
import {
  BlogEntry as BlogEntryInstance, BlogPageProps, ENTRIES_ID_MAP,
  ENTRIES_SLUG_MAP, FullBlogEntryData,
} from 'BlogEntries'
import { Layout, Main } from 'layout'
import { dateFormat } from 'lib/utils'
import Head from 'next/head'
import Image from 'next/image'
import Link from 'next/link'
import { ContactCard } from 'organisms'
import { useMemo } from 'react'
import { useWindowDimensions } from 'services/layout'

import Styles from './BlogEntry.module.sass'

import type { NextPage } from 'next'

const BlogEntry: NextPage<BlogPageProps> = ({
  year = '',
  month = '',
  blogEntryData,
}) => {
  const { width: windowWidth } = useWindowDimensions()
  const {
    date,
    image,
    ogImage,
    title,
    teaser,
    url,
    tags,
    linkedBlogEntryIndexes,
    BlogContent,
  } = useMemo<FullBlogEntryData>(() => {
    const e = ENTRIES_SLUG_MAP[[
      year,
      month,
      blogEntryData?.url
    ].join('/')]
    return e?.toFullJSON() || {} as FullBlogEntryData
  }, [blogEntryData?.url, month, year])

  //
  // Load linked articles
  const linkedEntries = useMemo<BlogEntryInstance[]>(() => {
    if (!linkedBlogEntryIndexes) return []
    if (linkedBlogEntryIndexes.length <= 0) return []

    return linkedBlogEntryIndexes.map(
      (id) => ENTRIES_ID_MAP[id]
    )
  }, [linkedBlogEntryIndexes])

  //
  // Check if sticky sidebar is enabled
  const stickySideEnabled = useMemo(
    () => windowWidth >= 768,
    [windowWidth]
  )

  //
  // Render
  return (
    <Layout>
      <Head>
        <title>
          {title} | Blog | FrauenCoach
        </title>
        <meta key="description" name="description" content={teaser} />
        <meta key="keywords" name="keywords" content={tags?.join(', ')} />
        <meta key="ogType" property="og:type" content="article" />
        <meta key="ogDescription" property="og:description" content={teaser} />
        <meta key="ogImage" property="og:image" content={ogImage?.src} />
        <meta key="twitterImage" property="twitter:image" content={ogImage?.src} />
      </Head>
      <Main>
        <Section background="rose">
          <Container>
            <h1>
              {title}
            </h1>
          </Container>
        </Section>
        <Section className={Styles.sectionMeta}>
          <Container>
            <div className="breadcrumbs">
              <Link href={`/blog/${year || ''}`}>
                {year}
              </Link>
              &nbsp;/&nbsp;
              <Link href={`/blog/${year || ''}/${month || ''}`}>
                {month}
              </Link>
              &nbsp;/&nbsp;
              {url}
            </div>
            {date && (
              <div className="date">
                {dateFormat(new Date(date), 'dd.MM.yyyy')}
              </div>
            )}
          </Container>
        </Section>
        <Section className={Styles.sectionTeaser}>
          <Container>
            <p className="text-center">
              {teaser}
            </p>
          </Container>
        </Section>
        <Section id="content" className={Styles.sectionContent}>
          <Container>
            <StickyContainer enabled={stickySideEnabled}>
              <Row>
                <Col xs={12} md={4}>
                  <Sticky
                    enabled={stickySideEnabled}
                    topOffset={stickySideEnabled ? -130 : 0}
                  >
                    {({ style, isSticky, distanceFromBottom }) => (
                      <div style={stickySideEnabled ? {
                        ...style,
                        top: isSticky ? '130px' : '',
                        transform: distanceFromBottom - 100 <= 0
                          ? `translateY(${distanceFromBottom - 100}px)`
                          : 'translateY(0px)'
                      } : {}}>
                        <div className={Styles.contentLeftImage}>
                          <Image
                            src={image}
                            alt={title}
                            placeholder="blur"
                            sizes="(max-width: 767px) 513px,
                                    (max-width: 991px) 216px,
                                    (max-width: 1199px) 296px,
                                    416px"
                          />
                        </div>
                        <hr />
                        <Tags tags={tags} />
                        <hr className="d-none d-md-block" />
                        <ShareButtons
                          className="d-none d-md-block"
                          title={`${title} | Blog | FrauenCoach`}
                          summary={teaser}
                        />
                        <br className="d-none d-md-block" />
                      </div>
                    )}
                  </Sticky>
                </Col>
                <Col xs={12} md={8} className={Styles.contentRight}>
                  <BlogContent />
                  <hr className="d-block d-md-none" />
                  <ShareButtons
                    className="d-block d-md-none"
                    title={`${title} | Blog | FrauenCoach`}
                    summary={teaser}
                    size={64}
                  />
                </Col>
              </Row>
            </StickyContainer>
          </Container>
        </Section>
        <Section background="vintage">
          <Container>
            <ContactCard />
          </Container>
        </Section>
        {linkedEntries?.length > 0 && (
          <Section>
            <Container>
              <BlogList blogEntries={linkedEntries} />
            </Container>
          </Section>
        )}
      </Main>
    </Layout>
  )
}

export default BlogEntry
